const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidLatitude = (value) => {
  const numeric = toNumber(value);
  return numeric !== null && Math.abs(numeric) <= 90;
};

const isValidLongitude = (value) => {
  const numeric = toNumber(value);
  return numeric !== null && Math.abs(numeric) <= 180;
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const fromLat = toNumber(lat1);
  const fromLon = toNumber(lon1);
  const toLat = toNumber(lat2);
  const toLon = toNumber(lon2);

  if (
    fromLat === null ||
    fromLon === null ||
    toLat === null ||
    toLon === null
  ) {
    return null;
  }

  const R = 6371;
  const dLat = ((toLat - fromLat) * Math.PI) / 180;
  const dLon = ((toLon - fromLon) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((fromLat * Math.PI) / 180) *
      Math.cos((toLat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const resolveCoordinatePair = (longitude, latitude, userCoordinates = null) => {
  const standard = {
    longitude: toNumber(longitude),
    latitude: toNumber(latitude),
  };
  const swapped = {
    longitude: toNumber(latitude),
    latitude: toNumber(longitude),
  };

  const standardIsValid =
    standard.longitude !== null &&
    standard.latitude !== null &&
    isValidLongitude(standard.longitude) &&
    isValidLatitude(standard.latitude);
  const swappedIsValid =
    swapped.longitude !== null &&
    swapped.latitude !== null &&
    isValidLongitude(swapped.longitude) &&
    isValidLatitude(swapped.latitude);

  if (!userCoordinates || !standardIsValid || !swappedIsValid) {
    return standardIsValid ? standard : swappedIsValid ? swapped : null;
  }

  const standardDistance = getDistanceKm(userCoordinates, standard);
  const swappedDistance = getDistanceKm(userCoordinates, swapped);

  if (standardDistance === null) return swapped;
  if (swappedDistance === null) return standard;

  return swappedDistance < standardDistance ? swapped : standard;
};

export const getItemCoordinates = (item, userCoordinates = null) => {
  const locationLabel = getItemLocationLabel(item);
  const areaCoordinates = getAreaCoordinates(locationLabel.locality, locationLabel.city);
  if (areaCoordinates) return areaCoordinates;

  const directCoordinates =
    item?.geoJson?.coordinates ||
    item?.geojson?.coordinates ||
    item?.location?.coordinates ||
    item?.address?.coordinates ||
    item?.address?.geoJson?.coordinates ||
    item?.coordinates;

  if (Array.isArray(directCoordinates) && directCoordinates.length >= 2) {
    const resolved = resolveCoordinatePair(
      directCoordinates[0],
      directCoordinates[1],
      userCoordinates
    );
    if (resolved) return resolved;
  }

  if (
    directCoordinates &&
    typeof directCoordinates === "object" &&
    !Array.isArray(directCoordinates)
  ) {
    const latitude = toNumber(
      directCoordinates.latitude ?? directCoordinates.lat
    );
    const longitude = toNumber(
      directCoordinates.longitude ?? directCoordinates.lng
    );
    if (latitude !== null && longitude !== null) {
      return { latitude, longitude };
    }
  }

  const latitude = toNumber(
    item?.latitude ?? item?.lat ?? item?.address?.latitude ?? item?.address?.lat
  );
  const longitude = toNumber(
    item?.longitude ??
      item?.lng ??
      item?.lon ??
      item?.address?.longitude ??
      item?.address?.lng
  );
  if (latitude !== null && longitude !== null) {
    return { latitude, longitude };
  }

  return null;
};

export const getItemDistance = (item) => {
  const backendDistance = toNumber(item?.distanceKm ?? item?.distance);
  if (backendDistance !== null) {
    return backendDistance;
  }

  return null;
};

export const getItemLocationLabel = (item) => {
  const locality =
    item?.locality ||
    item?.address?.locality ||
    item?.location?.locality ||
    item?.address?.suburb ||
    "";
  const city =
    item?.city ||
    item?.address?.city ||
    item?.location?.city ||
    item?.address?.town ||
    "";

  return {
    locality: `${locality}`.trim(),
    city: `${city}`.trim(),
  };
};

export const getDistanceKm = (from, to) => {
  if (!from || !to) return null;

  const lat1 = toNumber(from.latitude);
  const lon1 = toNumber(from.longitude);
  const lat2 = toNumber(to.latitude);
  const lon2 = toNumber(to.longitude);

  if (
    lat1 === null ||
    lon1 === null ||
    lat2 === null ||
    lon2 === null
  ) {
    return null;
  }

  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

export const NEARBY_RADIUS_KM = 15;

export const getItemDistanceFromUser = (item, userCoordinates) => {
  if (!userCoordinates) return null;

  const itemCoords = getItemCoordinates(item, userCoordinates);
  const computedDistance = getDistanceKm(userCoordinates, itemCoords);
  if (computedDistance !== null) return computedDistance;

  return getItemDistance(item);
};

export const filterItemsWithinRadius = (
  items = [],
  userCoordinates,
  radiusKm = NEARBY_RADIUS_KM
) => {
  const hasUserCoordinates =
    userCoordinates &&
    Number.isFinite(userCoordinates.latitude) &&
    Number.isFinite(userCoordinates.longitude);

  if (!hasUserCoordinates) return [];

  return items.filter((item) => {
    const distance = getItemDistanceFromUser(item, userCoordinates);
    return distance !== null && distance <= radiusKm;
  });
};

const resolveDistance = (item, userCoordinates) => {
  const itemCoordinates = getItemCoordinates(item, userCoordinates);
  if (userCoordinates && itemCoordinates) {
    return calculateDistance(
      userCoordinates.latitude,
      userCoordinates.longitude,
      itemCoordinates.latitude,
      itemCoordinates.longitude
    );
  }

  return getItemDistance(item);
};

const normalize = (value) => `${value || ""}`.trim().toLowerCase();

const AREA_COORDINATES = {
  thane: { latitude: 19.2183, longitude: 72.9781 },
  "thane west": { latitude: 19.2183, longitude: 72.9781 },
  "thane east": { latitude: 19.1941, longitude: 72.9725 },
  "mira road": { latitude: 19.2876, longitude: 72.8681 },
  "mira-bhayandar": { latitude: 19.2952, longitude: 72.8544 },
  bhayandar: { latitude: 19.3015, longitude: 72.851 },
  dahisar: { latitude: 19.2502, longitude: 72.8594 },
  "dahisar east": { latitude: 19.2576, longitude: 72.8681 },
  "dahisar west": { latitude: 19.2502, longitude: 72.8594 },
  powai: { latitude: 19.1197, longitude: 72.905 },
  andheri: { latitude: 19.1136, longitude: 72.8697 },
  "andheri east": { latitude: 19.1136, longitude: 72.8697 },
  "andheri west": { latitude: 19.1364, longitude: 72.8277 },
  bandra: { latitude: 19.0596, longitude: 72.8295 },
  "bandra west": { latitude: 19.0596, longitude: 72.8295 },
  malad: { latitude: 19.1874, longitude: 72.8484 },
  "malad west": { latitude: 19.1874, longitude: 72.8484 },
  goregaon: { latitude: 19.1663, longitude: 72.8526 },
  borivali: { latitude: 19.2307, longitude: 72.8567 },
  kandivali: { latitude: 19.2045, longitude: 72.8479 },
  mulund: { latitude: 19.1765, longitude: 72.957 },
  airoli: { latitude: 19.1558, longitude: 73.0076 },
  ghansoli: { latitude: 19.125, longitude: 73.005 },
  "kopar khairane": { latitude: 19.103, longitude: 73.01 },
  "navi mumbai": { latitude: 19.033, longitude: 73.0297 },
  rabale: { latitude: 19.136, longitude: 73.003 },
  mahape: { latitude: 19.117, longitude: 73.027 },
  ghatkopar: { latitude: 19.086, longitude: 72.908 },
  chembur: { latitude: 19.0522, longitude: 72.9005 },
  vashi: { latitude: 19.0771, longitude: 72.9986 },
  nerul: { latitude: 19.033, longitude: 73.0195 },
  panvel: { latitude: 18.9894, longitude: 73.1175 },
  kalyan: { latitude: 19.2403, longitude: 73.1305 },
  dombivli: { latitude: 19.2183, longitude: 73.088 },
  bhandup: { latitude: 19.1486, longitude: 72.9376 },
  vikhroli: { latitude: 19.1115, longitude: 72.928 },
  worli: { latitude: 19.0176, longitude: 72.817 },
  "lower parel": { latitude: 19.0006, longitude: 72.83 },
  colaba: { latitude: 18.9067, longitude: 72.8147 },
  juhu: { latitude: 19.1075, longitude: 72.8263 },
};

const getAreaCoordinates = (...labels) => {
  for (const label of labels) {
    const normalized = normalize(label);
    if (!normalized) continue;
    if (AREA_COORDINATES[normalized]) return AREA_COORDINATES[normalized];

    const match = Object.keys(AREA_COORDINATES)
      .sort((left, right) => right.length - left.length)
      .find((area) => normalized.includes(area));
    if (match) return AREA_COORDINATES[match];
  }

  return null;
};

const areasOverlap = (left, right) => {
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
};

const isCurrentArea = (locality, city, userLocality, userCity) => {
  if (userLocality && (areasOverlap(locality, userLocality) || areasOverlap(city, userLocality))) {
    return true;
  }

  if (!userLocality && userCity && (areasOverlap(city, userCity) || areasOverlap(locality, userCity))) {
    return true;
  }

  return false;
};

export const sortByLocation = (items = [], options = {}) => {
  const {
    coordinates: userCoordinates = null,
    locality: userLocality = "",
    city: userCity = "",
  } = options;

  const normalizedUserLocality = normalize(userLocality);
  const normalizedUserCity = normalize(userCity);
  const hasUserCoordinates =
    userCoordinates &&
    Number.isFinite(userCoordinates.latitude) &&
    Number.isFinite(userCoordinates.longitude);

  return [...items].sort((left, right) => {
    if (hasUserCoordinates) {
      const leftDistance = resolveDistance(left, userCoordinates);
      const rightDistance = resolveDistance(right, userCoordinates);

      if (leftDistance !== null || rightDistance !== null) {
        if (leftDistance === null) return 1;
        if (rightDistance === null) return -1;
        if (leftDistance !== rightDistance) return leftDistance - rightDistance;
      }
    }

    const leftLocation = getItemLocationLabel(left);
    const rightLocation = getItemLocationLabel(right);
    const leftLocality = normalize(leftLocation.locality);
    const rightLocality = normalize(rightLocation.locality);
    const leftCity = normalize(leftLocation.city);
    const rightCity = normalize(rightLocation.city);

    if (normalizedUserLocality || normalizedUserCity) {
      const leftInCurrentArea = isCurrentArea(
        leftLocality,
        leftCity,
        normalizedUserLocality,
        normalizedUserCity
      );
      const rightInCurrentArea = isCurrentArea(
        rightLocality,
        rightCity,
        normalizedUserLocality,
        normalizedUserCity
      );

      if (leftInCurrentArea !== rightInCurrentArea) {
        return leftInCurrentArea ? -1 : 1;
      }
    }

    const leftLabel =
      leftLocality || leftCity || normalize(left?.name || left?.title);
    const rightLabel =
      rightLocality || rightCity || normalize(right?.name || right?.title);

    return leftLabel.localeCompare(rightLabel);
  });
};

export const sortClubsByDistance = (items = [], userCoordinates = null) => {
  const hasUserCoordinates =
    userCoordinates &&
    Number.isFinite(userCoordinates.latitude) &&
    Number.isFinite(userCoordinates.longitude);

  return [...items].sort((left, right) => {
    const leftDistance = resolveDistance(
      left,
      hasUserCoordinates ? userCoordinates : null
    );
    const rightDistance = resolveDistance(
      right,
      hasUserCoordinates ? userCoordinates : null
    );

    if (leftDistance !== null || rightDistance !== null) {
      if (leftDistance === null) return 1;
      if (rightDistance === null) return -1;
      if (leftDistance !== rightDistance) {
        return leftDistance - rightDistance;
      }
    }

    return 0;
  });
};

export const formatDistanceKm = (distance, { fromMeters = false } = {}) => {
  const numericDistance = toNumber(distance);
  if (numericDistance === null) {
    return null;
  }

  const distanceKm =
    fromMeters || numericDistance > 1000
      ? numericDistance / 1000
      : numericDistance;
  const precision = distanceKm < 10 ? 1 : 0;
  return `${distanceKm.toFixed(precision)} km`;
};
